import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CourseService, SortConfig, TableSortingService } from '../../../services';
import { LoggingService } from '../../../services/shared/logging/logging.service';
import { Course } from '../../../models';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FilteringService } from '../../../services/shared/filtering/filtering.service';
import { SortableHeaderComponent } from '../../shared/sortable-header/sortable-header.component';
import { TablePaginationService, PaginationResult } from '../../../services/shared/table-pagination/table-pagination.service';
import { TablePaginationComponent, PageChangeEvent } from '../../shared/table-pagination/table-pagination.component';

@Component({
    selector: 'app-course-list',
    standalone: true,
    imports: [CommonModule, RouterLink, FormsModule, SortableHeaderComponent, TablePaginationComponent],
    templateUrl: './course-list.component.html',
    styleUrl: './course-list.component.scss',
})
export class CourseListComponent implements OnInit, OnDestroy {
    private loggingService = inject(LoggingService);
    private tableSortingService = inject(TableSortingService);
    private tablePaginationService = inject(TablePaginationService);

    // Signals for reactive state management
    searchText = signal<string>('');
    courses = signal<Course[]>([]);
    sortConfig = signal<SortConfig | null>(null);
    currentPage = signal<number>(1);
    pageSize = signal<number>(20);

    // Computed values
    filteredCourses = computed(() => {
        const allCourses = this.courses();
        const searchTerm = this.searchText();
        const sort = this.sortConfig();

        // Filter by search term
        let filtered = this.filteringService.filterItems(
            allCourses,
            searchTerm,
            ['name', 'shortName', 'tournamentName']
        );

        // Apply sorting if configured
        if (sort) {
            filtered = this.tableSortingService.sort(filtered, sort.key, sort.direction);
        }

        return filtered;
    });

    paginationResult = computed<PaginationResult<Course>>(() => {
        const filtered = this.filteredCourses();
        const page = this.currentPage();
        const size = this.pageSize();

        return this.tablePaginationService.paginate(filtered, page, size);
    });

    /**
     * Helper method to generate the course link path
     */
    getCourseLink(courseId: string | undefined): string {
        if (!courseId) return '#';
        return `/courses/${courseId}`;
    }

    constructor(
        private courseService: CourseService,
        private filteringService: FilteringService
    ) {}

    ngOnInit(): void {
        this.load();
    }

    ngOnDestroy(): void {
        // no subscriptions to clean up
    }

    private load(): void {
        this.courseService.getAll()?.subscribe({
            next: (courses: Course[]) => {
                this.courses.set(courses);
            },
            error: (error: any) => this.loggingService.error('Error loading courses:', error)
        });
    }

    filterCourses() {
        // Reset to first page when searching
        this.currentPage.set(1);
        // Trigger reactivity by calling the computed - it will handle the filtering
        this.filteredCourses();
    }

    clearSearch() {
        this.searchText.set('');
        this.currentPage.set(1);
        this.filterCourses();
    }

    onSort(event: { key: string; direction: 'asc' | 'desc' }) {
        this.sortConfig.set({ key: event.key, direction: event.direction });
    }

    onPageChange(event: PageChangeEvent) {
        this.currentPage.set(event.page);
    }
}
